import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import clsx from 'clsx';
import { forwardRef, type Ref } from 'react';

import { useColumns } from './hooks/useColumns.js';
import type {
  SimpleTableV2ColumnDefBase,
  SimpleTableV2Props,
  SimpleTableV2RowData,
} from './public.api.js';
import { SimpleTableV2CSS } from '../SimpleTableV2/SimpleTableV2.sty.js';

// https://tanstack.com/table/latest/docs/api/core/column-def#meta
declare module '@tanstack/react-table' {
  interface ColumnMeta<TData extends SimpleTableV2RowData, TValue> {
    alignment?: SimpleTableV2ColumnDefBase<TData, TValue>['alignment'];
    width?: SimpleTableV2ColumnDefBase<TData, TValue>['width'];
    minWidth?: SimpleTableV2ColumnDefBase<TData, TValue>['minWidth'];
    maxWidth?: SimpleTableV2ColumnDefBase<TData, TValue>['maxWidth'];
  }
}

/**
 * The `SimpleTableV2` component is a simplified version of the `DataTableV2`. It is
 * designed to handle small sets of data and markdown representation. It does not
 * feature sorting, resizing, filtering, or virtualization and behaves like a
 * native HTML table.
 */
function InternalSimpleTable<TData, TValue>(
  props: SimpleTableV2Props<TData>,
  forwardedRef: Ref<HTMLTableElement>,
) {
  const {
    data,
    columns,
    'data-testid': dataTestId,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
    variant,
    style: consumerStyle,
    className: consumerClassName,
    ...remainingProps
  } = props;

  // Hook used to adapt column definition for internal usage
  const extractedColumns = useColumns<TData, TValue>(columns);

  const table = useReactTable({
    data,
    columns: extractedColumns,
    getCoreRowModel: getCoreRowModel(),
  });

  const combinedVariants: Required<SimpleTableV2Props['variant']> = {
    rowSeparation: variant?.rowSeparation ?? 'horizontalDividers',
    verticalDividers: variant?.verticalDividers ?? false,
    contained: variant?.contained ?? true,
    rowDensity: variant?.rowDensity ?? 'default',
    fontStyle: variant?.fontStyle ?? 'text',
  };

  return (
    <table
      ref={forwardedRef}
      data-testid={dataTestId}
      data-dtrum-mask={dataDtrumMask}
      data-dtrum-allow={dataDtrumAllow}
      className={clsx(SimpleTableV2CSS, consumerClassName, {
        'strato-table-contained': combinedVariants.contained,
        'strato-table-colsep-divider': combinedVariants.verticalDividers,
        'strato-table-code-font': combinedVariants.fontStyle === 'code',
        'strato-table-cell-density-default':
          combinedVariants.rowDensity === 'default',
        'strato-table-cell-density-condensed':
          combinedVariants.rowDensity === 'condensed',
        'strato-table-cell-density-comfortable':
          combinedVariants.rowDensity === 'comfortable',
        'strato-table-rowsep-zebra':
          combinedVariants.rowSeparation === 'zebraStripes',
        'strato-table-rowsep-divider':
          combinedVariants.rowSeparation === 'horizontalDividers' ||
          combinedVariants.rowSeparation === 'zebraStripes',
      })}
      style={consumerStyle}
      {...remainingProps}
    >
      <thead>
        {table.getHeaderGroups().map((headerGroup) => (
          <tr key={headerGroup.id} className="strato-table-header">
            {headerGroup.headers.map((header) => (
              <th
                key={header.id}
                className={clsx('strato-table-header-cell', {
                  'strato-table-cell-alignment-left':
                    header.column.columnDef.meta?.alignment === 'left',
                  'strato-table-cell-alignment-center':
                    header.column.columnDef.meta?.alignment === 'center',
                  'strato-table-cell-alignment-right':
                    header.column.columnDef.meta?.alignment === 'right',
                  'strato-table-cell-alignment-top-left':
                    header.column.columnDef.meta?.alignment === 'top-left',
                  'strato-table-cell-alignment-top-center':
                    header.column.columnDef.meta?.alignment === 'top-center',
                  'strato-table-cell-alignment-top-right':
                    header.column.columnDef.meta?.alignment === 'top-right',
                  'strato-table-cell-alignment-bottom-left':
                    header.column.columnDef.meta?.alignment === 'bottom-left',
                  'strato-table-cell-alignment-bottom-center':
                    header.column.columnDef.meta?.alignment === 'bottom-center',
                  'strato-table-cell-alignment-bottom-right':
                    header.column.columnDef.meta?.alignment === 'bottom-right',
                })}
                style={{
                  minWidth: header.column.columnDef.meta?.minWidth,
                  width: header.column.columnDef.meta?.width,
                  maxWidth: header.column.columnDef.meta?.maxWidth,
                }}
              >
                {header.isPlaceholder
                  ? null
                  : flexRender(
                      header.column.columnDef.header,
                      header.getContext(),
                    )}
              </th>
            ))}
          </tr>
        ))}
      </thead>
      <tbody>
        {table.getRowModel().rows.map((row, rowIndex) => (
          <tr
            key={row.id}
            className={clsx('strato-table-row', {
              'strato-table-row-even': rowIndex % 2,
            })}
          >
            {row.getVisibleCells().map((cell) => (
              <td
                key={cell.id}
                className={clsx('strato-table-cell', {
                  'strato-table-cell-alignment-left':
                    cell.column.columnDef.meta?.alignment === 'left',
                  'strato-table-cell-alignment-center':
                    cell.column.columnDef.meta?.alignment === 'center',
                  'strato-table-cell-alignment-right':
                    cell.column.columnDef.meta?.alignment === 'right',
                  'strato-table-cell-alignment-top-left':
                    cell.column.columnDef.meta?.alignment === 'top-left',
                  'strato-table-cell-alignment-top-center':
                    cell.column.columnDef.meta?.alignment === 'top-center',
                  'strato-table-cell-alignment-top-right':
                    cell.column.columnDef.meta?.alignment === 'top-right',
                  'strato-table-cell-alignment-bottom-left':
                    cell.column.columnDef.meta?.alignment === 'bottom-left',
                  'strato-table-cell-alignment-bottom-center':
                    cell.column.columnDef.meta?.alignment === 'bottom-center',
                  'strato-table-cell-alignment-bottom-right':
                    cell.column.columnDef.meta?.alignment === 'bottom-right',
                })}
                style={{
                  minWidth: cell.column.columnDef.meta?.minWidth,
                  width: cell.column.columnDef.meta?.width,
                  maxWidth: cell.column.columnDef.meta?.maxWidth,
                }}
              >
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
InternalSimpleTable.displayName = 'SimpleTableV2';

/**
 * The `SimpleTableV2` component is a simplified version of the `DataTableV2`. It is
 * designed to handle small sets of data and markdown representation. It does not
 * feature sorting, resizing, filtering, or virtualization and behaves like a
 * native HTML table.
 * @public
 */
export const SimpleTableV2 = /* @__PURE__ */ forwardRef(InternalSimpleTable);
